import { authManager, AuthSystem } from '@/services/auth/authManager';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import {
  getPermissionGroups,
  getCurrentUserPermissions,
  type PermissionGroup,
  type UserPermission,
  type PermissionTreeNode,
  type PermissionConfigData,
  type RolePermissionAssignment,
  buildPermissionTree,
} from '@/services/permission/api';
import { message } from 'antd';
import { getPermissionScopeDescription, hasPermissionScope } from '@/utils/permissionUtils';
import { permissionStorageService } from '@/services/permission/storageService';
import { auth401Handler } from '@/utils/auth401Handler';

interface PermissionState {
  // Permission structure data
  permissionGroups: PermissionGroup[];
  permissionTree: PermissionTreeNode[];
  permissionCodes: Set<string>;

  // User permissions
  userPermissions: UserPermission[];
  userPermissionMap: Map<string, 'all' | 'team' | 'self'>;
  userId: string | null;
  teamIds: string[];
  permissionConfigSnapshot: PermissionConfigData | null;
  rolePermissionSnapshot: RolePermissionAssignment | null;

  // Loading states
  isLoadingGroups: boolean;
  isLoadingUserPermissions: boolean;
  isInitialized: boolean;

  // Error states
  groupsError: Error | null;
  userPermissionsError: Error | null;
  retryCount: number;
  is401Handling: boolean;

  // Timestamps
  lastGroupsFetch: number | null;
  lastUserPermissionsFetch: number | null;

  // Actions
  loadPermissionGroups: (force?: boolean) => Promise<void>;
  loadUserPermissions: (force?: boolean) => Promise<void>;
  getUserPermissions: () => string[];
  hasPermission: (code: string) => boolean;
  getPermissionScope: (code: string) => 'all' | 'team' | 'self' | null;
  getScopeDescription: (code: string) => string;
  hasScope: (code: string) => boolean;
  clearCache: () => void;
  initialize: () => Promise<void>;
  refreshAll: () => Promise<void>;
  loadOfflineData: () => Promise<boolean>;
  handle401Error: () => void;
  reset: () => void;
  setRecoveredPermissions: (permissions: any[]) => void;
}

// CACHING ENABLED - Cache permissions and refresh every 10 minutes
const CACHE_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // Start with 1 second, exponential backoff

// Global refresh timer
let refreshTimer: ReturnType<typeof setInterval> | null = null;

// Helper function for exponential backoff
const getRetryDelay = (retryCount: number) => RETRY_DELAY * Math.pow(2, retryCount);

// Global coordination state to prevent duplicate operations
let globalInitPromise: Promise<void> | null = null;
let permissionGroupsRequestId = 0;
let userPermissionsRequestId = 0;
let permissionSessionGeneration = 0;

export const usePermissionStore = create<PermissionState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        // Initial state
        permissionGroups: [],
        permissionTree: [],
        permissionCodes: new Set(),
        userPermissions: [],
        userPermissionMap: new Map(),
        userId: null,
        teamIds: [],
        permissionConfigSnapshot: null,
        rolePermissionSnapshot: null,
        isLoadingGroups: false,
        isLoadingUserPermissions: false,
        isInitialized: false,
        groupsError: null,
        userPermissionsError: null,
        retryCount: 0,
        is401Handling: false,
        lastGroupsFetch: null,
        lastUserPermissionsFetch: null,

        // Initialize the store - USE CACHING, load from cache first then refresh from API if needed
        initialize: async () => {
          const state = get();
          const sessionGeneration = permissionSessionGeneration;

          // Prevent concurrent initialization
          if (globalInitPromise) {
            console.debug('[PermissionStore] Initialization already in progress, waiting');
            return globalInitPromise;
          }

          // Check if we're handling 401 error
          if (state.is401Handling || auth401Handler.isHandling401()) {
            console.debug('[PermissionStore] Skipping initialization during 401 handling');
            return;
          }

          globalInitPromise = (async () => {
            try {
              console.log('[PermissionStore] Starting initialization with caching enabled');

              // First try to load cached data
              let cachedDataLoaded = false;
              try {
                const cachedData = permissionStorageService.loadPermissions();
                if (cachedData) {
                  console.log('[PermissionStore] Using cached permissions (valid for 10 minutes)');

                  // Set cached data immediately for fast UI rendering
                  const permMap = new Map<string, 'all' | 'team' | 'self'>();
                  cachedData.userPermissions.forEach((perm) => {
                    const viewScope =
                      perm.viewScope || (hasPermissionScope(perm.code) ? 'all' : 'self');
                    permMap.set(perm.code, viewScope);
                  });

                  const codes = new Set<string>();
                  cachedData.permissionGroups.forEach((group) => {
                    group.permissions.forEach((perm) => {
                      codes.add(perm.code);
                    });
                  });

                  const tree = buildPermissionTree(cachedData.permissionGroups);

                  if (sessionGeneration !== permissionSessionGeneration) {
                    return;
                  }

                  set({
                    permissionGroups: cachedData.permissionGroups,
                    permissionTree: tree,
                    permissionCodes: codes,
                    userPermissions: cachedData.userPermissions,
                    userPermissionMap: permMap,
                    userId: cachedData.userId,
                    teamIds: cachedData.teamIds,
                    permissionConfigSnapshot: cachedData.permissionConfig ?? null,
                    rolePermissionSnapshot: cachedData.rolePermissions ?? null,
                    lastGroupsFetch: cachedData.timestamp,
                    lastUserPermissionsFetch: cachedData.timestamp,
                    isInitialized: true,
                    retryCount: 0,
                  });

                  cachedDataLoaded = true;
                  console.log(
                    '[PermissionStore] Cached data loaded successfully, refreshing from API',
                  );
                }
              } catch (error) {
                console.error('[PermissionStore] Failed to load cached data:', error);
              }

              // Fetch from API after rendering cached data so role changes become visible
              // immediately after login/reload instead of waiting for the cache TTL.
              if (!cachedDataLoaded) {
                try {
                  console.log(
                    '[PermissionStore] No cached data found, fetching fresh permissions from API',
                  );
                  await Promise.all([
                    get().loadPermissionGroups(true),
                    get().loadUserPermissions(true),
                  ]);
                  console.log('[PermissionStore] Fresh API data loaded and cached');
                } catch (error) {
                  console.error('[PermissionStore] Failed to load fresh data from API:', error);
                  console.error('[PermissionStore] Error details:', {
                    message: (error as any)?.message,
                    status: (error as any)?.response?.status,
                    statusText: (error as any)?.response?.statusText,
                    data: (error as any)?.response?.data,
                  });

                  set({ isInitialized: false });
                  throw error;
                }
              } else {
                Promise.all([get().loadPermissionGroups(true), get().loadUserPermissions(true)])
                  .then(() => {
                    console.log('[PermissionStore] Cached permissions refreshed from API');
                  })
                  .catch((error) => {
                    console.error('[PermissionStore] Failed to refresh cached permissions:', error);
                  });
              }

              // Mark as initialized
              if (sessionGeneration !== permissionSessionGeneration) {
                return;
              }
              set({ isInitialized: true });
              console.log('[PermissionStore] Initialization completed');
            } catch (error) {
              console.error('[PermissionStore] Initialization failed:', error);
              if (sessionGeneration === permissionSessionGeneration) {
                set({ isInitialized: false });
              }
            } finally {
              globalInitPromise = null;
            }
          })();

          return globalInitPromise;
        },

        // Load permission groups - NO CACHING, always fetch fresh from API
        loadPermissionGroups: async (force = false) => {
          const state = get();

          // Prevent concurrent loads and loads during 401 handling
          if (state.isLoadingGroups || state.is401Handling || auth401Handler.isHandling401()) {
            console.debug(
              '[PermissionStore] Permission groups already loading or 401 handling, skipping',
            );
            return;
          }

          // Check authentication before loading
          if (!authManager.isAuthenticated(AuthSystem.ADMIN)) {
            console.debug(
              '[PermissionStore] Skipping permission groups load - user not authenticated',
            );
            return;
          }

          const requestToken = authManager.getToken(AuthSystem.ADMIN);
          const requestId = ++permissionGroupsRequestId;

          set({ isLoadingGroups: true, groupsError: null });

          try {
            console.log('[PermissionStore] Fetching permission groups from API and caching');
            const groups = await getPermissionGroups();

            if (
              requestId !== permissionGroupsRequestId ||
              !authManager.isCurrentToken(AuthSystem.ADMIN, requestToken)
            ) {
              console.debug('[PermissionStore] Discarding groups from a replaced session');
              return;
            }

            // Extract all permission codes
            const codes = new Set<string>();
            groups.forEach((group) => {
              group.permissions.forEach((perm) => {
                codes.add(perm.code);
              });
            });

            // Build permission tree
            const tree = buildPermissionTree(groups);

            const timestamp = Date.now();
            set({
              permissionGroups: groups,
              permissionTree: tree,
              permissionCodes: codes,
              lastGroupsFetch: timestamp,
              isLoadingGroups: false,
              retryCount: 0,
            });

            // Save to cache if we also have user permissions
            const currentState = get();
            if (currentState.userPermissions.length > 0 && currentState.userId) {
              permissionStorageService.savePermissions({
                permissionGroups: groups,
                userPermissions: currentState.userPermissions,
                userId: currentState.userId,
                teamIds: currentState.teamIds,
                permissionConfig: currentState.permissionConfigSnapshot,
                rolePermissions: currentState.rolePermissionSnapshot,
              });
            }

            console.log('[PermissionStore] Permission groups loaded and cached successfully');
          } catch (error) {
            if (
              requestId !== permissionGroupsRequestId ||
              !authManager.isCurrentToken(AuthSystem.ADMIN, requestToken)
            ) {
              console.debug('[PermissionStore] Ignoring groups error from a replaced session');
              return;
            }

            const retry = state.retryCount < MAX_RETRY_COUNT;

            set({
              groupsError: error as Error,
              isLoadingGroups: false,
              retryCount: retry ? state.retryCount + 1 : state.retryCount,
            });

            console.error('[PermissionStore] Failed to load permission groups:', error);
            console.error('[PermissionStore] Groups error details:', {
              message: (error as any)?.message,
              status: (error as any)?.response?.status,
              statusText: (error as any)?.response?.statusText,
              url: (error as any)?.request?.responseURL || (error as any)?.config?.url,
            });

            // Check if this is a 401 error
            const is401 = (error as any)?.response?.status === 401;
            if (is401) {
              get().handle401Error();
              return;
            }

            // Retry with exponential backoff only if not handling 401
            if (retry && !get().is401Handling && !auth401Handler.isHandling401()) {
              // Check if we still have a valid token before retrying
              if (authManager.isAuthenticated(AuthSystem.ADMIN)) {
                const retryTimer = setTimeout(() => {
                  // Triple check we're not in 401 handling mode before retry
                  if (!get().is401Handling && !auth401Handler.isHandling401()) {
                    get().loadPermissionGroups(force);
                  }
                }, getRetryDelay(state.retryCount));

                // Store timer reference for cleanup
                (window as any).__permissionRetryTimer = retryTimer;
              }
            } else {
              // Only show error if user is authenticated (not on login page)
              const isLoginPage = window.location.pathname === '/user/login';
              if (!isLoginPage && !get().is401Handling && !auth401Handler.isHandling401()) {
                message.error('Failed to load fresh permissions. Please refresh the page.');
              }
            }
          }
        },

        // Load user permissions - NO CACHING, always fetch fresh from API
        loadUserPermissions: async (force = false) => {
          const state = get();

          // Prevent concurrent loads and loads during 401 handling
          if (
            (state.isLoadingUserPermissions && !force) ||
            state.is401Handling ||
            auth401Handler.isHandling401()
          ) {
            console.debug(
              '[PermissionStore] User permissions already loading or 401 handling, skipping',
            );
            return;
          }

          // Check authentication before loading
          if (!authManager.isAuthenticated(AuthSystem.ADMIN)) {
            console.debug(
              '[PermissionStore] Skipping user permissions load - user not authenticated',
            );
            return;
          }

          const requestToken = authManager.getToken(AuthSystem.ADMIN);
          const requestId = ++userPermissionsRequestId;

          set({ isLoadingUserPermissions: true, userPermissionsError: null });

          // Set load start time for timeout tracking
          (window as any).__permissionLoadStartTime = Date.now();

          try {
            console.log('[PermissionStore] Fetching user permissions from API and caching');
            const response = await getCurrentUserPermissions(force);

            if (
              requestId !== userPermissionsRequestId ||
              !authManager.isCurrentToken(AuthSystem.ADMIN, requestToken)
            ) {
              console.debug('[PermissionStore] Discarding permissions from a replaced session');
              return;
            }

            const incomingConfigSnapshot = response.permissionConfig ?? null;
            const incomingRoleSnapshot = response.rolePermissions ?? null;

            // Validate response
            if (!response || !response.permissions) {
              console.error('[PermissionStore] Invalid response from permissions API:', response);
              // Don't throw - just set empty permissions to prevent infinite loops
              set({
                userPermissions: [],
                userPermissionMap: new Map(),
                userId: response?.userId || '',
                teamIds: response?.teamIds || [],
                permissionConfigSnapshot: response?.permissionConfig ?? incomingConfigSnapshot,
                rolePermissionSnapshot: response?.rolePermissions ?? incomingRoleSnapshot,
                lastUserPermissionsFetch: Date.now(),
                isLoadingUserPermissions: false,
                userPermissionsError: null,
                retryCount: 0,
              });
              return;
            }

            const mergedPermissionMap = new Map<string, UserPermission>();
            response.permissions.forEach((permission) => {
              mergedPermissionMap.set(permission.code, permission);
            });
            (response.rolePermissions?.permissions || []).forEach((permission) => {
              if (!mergedPermissionMap.has(permission.permissionCode)) {
                mergedPermissionMap.set(permission.permissionCode, {
                  code: permission.permissionCode,
                  viewScope: permission.viewScope,
                });
              }
            });
            const mergedPermissions = Array.from(mergedPermissionMap.values());

            // Create permission map for quick lookup
            const permMap = new Map<string, 'all' | 'team' | 'self'>();
            mergedPermissions.forEach((perm) => {
              const viewScope = perm.viewScope || (hasPermissionScope(perm.code) ? 'all' : 'self');
              permMap.set(perm.code, viewScope);
            });

            const timestamp = Date.now();
            set({
              userPermissions: mergedPermissions,
              userPermissionMap: permMap,
              userId: response.userId,
              teamIds: response.teamIds || [],
              permissionConfigSnapshot: incomingConfigSnapshot,
              rolePermissionSnapshot: incomingRoleSnapshot,
              lastUserPermissionsFetch: timestamp,
              isLoadingUserPermissions: false,
              retryCount: 0,
            });

            // Save to cache (if we also have permission groups)
            const updatedState = get();
            if (updatedState.permissionGroups.length > 0) {
              permissionStorageService.savePermissions({
                permissionGroups: updatedState.permissionGroups,
                userPermissions: mergedPermissions,
                userId: response.userId,
                teamIds: response.teamIds || [],
                permissionConfig: incomingConfigSnapshot,
                rolePermissions: incomingRoleSnapshot,
              });
            }

            console.log('[PermissionStore] User permissions loaded and cached successfully');
          } catch (error) {
            if (
              requestId !== userPermissionsRequestId ||
              !authManager.isCurrentToken(AuthSystem.ADMIN, requestToken)
            ) {
              console.debug('[PermissionStore] Ignoring permission error from a replaced session');
              return;
            }

            const is401 = (error as any)?.response?.status === 401;

            set({
              userPermissionsError: error as Error,
              isLoadingUserPermissions: false,
            });

            console.error('[PermissionStore] Failed to load user permissions:', error);
            console.error('[PermissionStore] User permissions error details:', {
              message: (error as any)?.message,
              status: (error as any)?.response?.status,
              statusText: (error as any)?.response?.statusText,
              url: (error as any)?.request?.responseURL || (error as any)?.config?.url,
            });

            // Handle 401 error
            if (is401) {
              get().handle401Error();
              return;
            }

            // Only retry for network errors, not for 401 or data errors
            const isNetworkError = !is401 && !(error as any)?.response;
            const shouldRetry =
              state.retryCount < MAX_RETRY_COUNT && isNetworkError && !state.is401Handling;

            if (shouldRetry) {
              set({ retryCount: state.retryCount + 1 });

              // Check if we still have a valid token before retrying
              if (authManager.isAuthenticated(AuthSystem.ADMIN)) {
                const retryTimer = setTimeout(() => {
                  // Triple check we're not in 401 handling mode before retry
                  if (!get().is401Handling && !auth401Handler.isHandling401()) {
                    console.log(
                      `[PermissionStore] Retrying fresh user permissions load (attempt ${state.retryCount + 1}/${MAX_RETRY_COUNT})`,
                    );
                    get().loadUserPermissions(force);
                  }
                }, getRetryDelay(state.retryCount));

                // Store timer reference for cleanup
                (window as any).__userPermissionRetryTimer = retryTimer;
              }
            } else {
              console.error(
                '[PermissionStore] Failed to load fresh user permissions after retries:',
                error,
              );
              // NO FALLBACK CACHE - permissions must come fresh from API
            }
          }
        },

        // Check if user has a permission
        hasPermission: (code: string) => {
          const { userPermissionMap } = get();
          return userPermissionMap.has(code);
        },

        // Get permission scope
        getPermissionScope: (code: string) => {
          const { userPermissionMap } = get();
          return userPermissionMap.get(code) || null;
        },

        // Get user permissions as array of codes
        getUserPermissions: () => {
          const { userPermissions } = get();
          return userPermissions.map((p) => p.code);
        },

        // Get scope description for a permission
        getScopeDescription: (code: string) => {
          const scope = get().getPermissionScope(code);
          return getPermissionScopeDescription(code, scope);
        },

        // Check if permission has scope support
        hasScope: (code: string) => {
          return hasPermissionScope(code);
        },

        // Clear temporary data and reset timestamps
        clearCache: () => {
          console.debug('[PermissionStore] Clearing temporary permission data');
          permissionStorageService.clearPermissions();
          set({
            lastGroupsFetch: null,
            lastUserPermissionsFetch: null,
            permissionConfigSnapshot: null,
            rolePermissionSnapshot: null,
          });
        },

        // Refresh all permissions from API - NO CACHING
        refreshAll: async () => {
          console.log('[PermissionStore] Refreshing all permissions from API (NO CACHING)');
          const state = get();
          await Promise.all([state.loadPermissionGroups(true), state.loadUserPermissions(true)]);
        },

        // Load temporary data if available (only for page refresh scenarios)
        loadOfflineData: async () => {
          console.debug(
            '[PermissionStore] Attempting to load temporary permissions for page refresh',
          );
          try {
            const tempData = permissionStorageService.loadTemporaryPermissions();
            if (tempData) {
              const permMap = new Map<string, 'all' | 'team' | 'self'>();
              tempData.userPermissions.forEach((perm) => {
                const viewScope =
                  perm.viewScope || (hasPermissionScope(perm.code) ? 'all' : 'self');
                permMap.set(perm.code, viewScope);
              });

              const codes = new Set<string>();
              tempData.permissionGroups.forEach((group) => {
                group.permissions.forEach((perm) => {
                  codes.add(perm.code);
                });
              });

              const tree = buildPermissionTree(tempData.permissionGroups);

              set({
                permissionGroups: tempData.permissionGroups,
                permissionTree: tree,
                permissionCodes: codes,
                userPermissions: tempData.userPermissions,
                userPermissionMap: permMap,
                userId: tempData.userId,
                teamIds: tempData.teamIds,
                permissionConfigSnapshot: tempData.permissionConfig ?? null,
                rolePermissionSnapshot: tempData.rolePermissions ?? null,
                lastGroupsFetch: tempData.timestamp,
                lastUserPermissionsFetch: tempData.timestamp,
                isInitialized: true,
              });

              console.debug('[PermissionStore] Successfully loaded temporary permissions');
              return true;
            }
            return false;
          } catch (error) {
            console.error('[PermissionStore] Failed to load temporary data:', error);
            return false;
          }
        },

        // Handle 401 error - stop all retries and reset state
        handle401Error: () => {
          console.warn('[PermissionStore] Handling 401 error, stopping all operations');
          set({
            is401Handling: true,
            isLoadingGroups: false,
            isLoadingUserPermissions: false,
            groupsError: null,
            userPermissionsError: null,
            retryCount: 0,
          });
        },

        // Reset the store state and clear temporary data
        reset: () => {
          console.debug('[PermissionStore] Resetting store state and clearing temporary data');

          // Invalidate requests started by the previous session so their late
          // responses cannot overwrite the newly authenticated user's state.
          permissionGroupsRequestId += 1;
          userPermissionsRequestId += 1;
          permissionSessionGeneration += 1;

          // Clear temporary data
          permissionStorageService.clearPermissions();

          set({
            permissionGroups: [],
            permissionTree: [],
            permissionCodes: new Set(),
            userPermissions: [],
            userPermissionMap: new Map(),
            userId: null,
            teamIds: [],
            permissionConfigSnapshot: null,
            rolePermissionSnapshot: null,
            isLoadingGroups: false,
            isLoadingUserPermissions: false,
            isInitialized: false,
            groupsError: null,
            userPermissionsError: null,
            retryCount: 0,
            is401Handling: false,
            lastGroupsFetch: null,
            lastUserPermissionsFetch: null,
          });

          console.debug('[PermissionStore] Store reset completed and temporary data cleared');
        },

        // Set recovered permissions from error recovery
        setRecoveredPermissions: (permissions: any[]) => {
          console.log('[PermissionStore] Setting recovered permissions:', permissions.length);

          // Create permission map
          const permMap = new Map<string, 'all' | 'team' | 'self'>();
          permissions.forEach((perm) => {
            const viewScope = perm.viewScope || (hasPermissionScope(perm.code) ? 'all' : 'self');
            permMap.set(perm.code, viewScope);
          });

          set({
            userPermissions: permissions,
            userPermissionMap: permMap,
            isLoadingUserPermissions: false,
            userPermissionsError: null,
            retryCount: 0,
            lastUserPermissionsFetch: Date.now(),
          });

          // Save to emergency permissions
          const codes = permissions.map((p) => p.code);
          localStorage.setItem('emergency_permissions', JSON.stringify(codes));
        },
      }),
      {
        name: 'PermissionStore',
      },
    ),
  ),
);

// Set up 10-minute refresh timer and visibility change listener
const setupPermissionRefresh = () => {
  // Clear any existing timer
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }

  // Set up 10-minute refresh timer
  refreshTimer = setInterval(async () => {
    // Check authentication before refreshing permissions
    if (!authManager.isAuthenticated(AuthSystem.ADMIN)) {
      console.log('[PermissionStore] User not authenticated, skipping scheduled refresh');
      return;
    }

    const state = usePermissionStore.getState();
    if (!state.is401Handling && !state.isLoadingGroups && !state.isLoadingUserPermissions) {
      console.log(
        '[PermissionStore] 10-minute scheduled refresh starting - updating permissions and role-permission data',
      );

      // Store current permissions to compare
      const currentPermissions = state.userPermissions
        .map((p) => p.code)
        .sort()
        .join(',');

      Promise.all([state.loadPermissionGroups(true), state.loadUserPermissions(true)])
        .then(() => {
          // Check if permissions actually changed
          const newPermissions = state.userPermissions
            .map((p) => p.code)
            .sort()
            .join(',');
          if (currentPermissions === newPermissions) {
            console.log(
              '[PermissionStore] 10-minute scheduled refresh completed - permissions unchanged, no UI update needed',
            );
          } else {
            console.log(
              '[PermissionStore] 10-minute scheduled refresh completed - permissions changed, UI will update',
            );
          }
        })
        .catch((error) => {
          console.error('[PermissionStore] Scheduled refresh failed:', error);
        });
    }
  }, CACHE_REFRESH_INTERVAL);

  console.log('[PermissionStore] 10-minute refresh timer started');
};

// Clear timer on logout
const clearPermissionRefresh = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
    console.log('[PermissionStore] Refresh timer cleared');
  }
};

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  // Start refresh timer when store is created
  setupPermissionRefresh();

  // Listen for login/logout events
  window.addEventListener('storage', (e) => {
    if (e.key === 'token') {
      if (e.newValue) {
        // A token replacement in another tab is a new session boundary.
        usePermissionStore.getState().reset();
        setupPermissionRefresh();
      } else {
        // User logged out, clear timer
        clearPermissionRefresh();
      }
    }
  });

  // Same-tab localStorage changes do not emit a storage event. The auth manager
  // sends this event so permission state and its refresh timer are reset immediately.
  window.addEventListener('admin-auth-cleared', () => {
    clearPermissionRefresh();
    usePermissionStore.getState().reset();
  });

  // Listen for visibility change to refresh permissions if cache is stale
  let visibilityChangeTimeout: ReturnType<typeof setTimeout> | null = null;
  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden) {
      // Add debounce to prevent rapid firing when switching between tabs quickly
      if (visibilityChangeTimeout) {
        clearTimeout(visibilityChangeTimeout);
      }

      visibilityChangeTimeout = setTimeout(async () => {
        // Check authentication before refreshing permissions
        if (!authManager.isAuthenticated(AuthSystem.ADMIN)) {
          return;
        }

        const state = usePermissionStore.getState();

        // Skip if already loading to prevent multiple simultaneous requests
        if (state.isLoadingGroups || state.isLoadingUserPermissions) {
          console.log('[PermissionStore] Already loading permissions, skipping visibility refresh');
          return;
        }

        // Only refresh if cache might be stale (last fetch > 10 minutes ago)
        const now = Date.now();
        const groupsAge = state.lastGroupsFetch ? now - state.lastGroupsFetch : Infinity;
        const userPermissionsAge = state.lastUserPermissionsFetch
          ? now - state.lastUserPermissionsFetch
          : Infinity;

        if (groupsAge > CACHE_REFRESH_INTERVAL || userPermissionsAge > CACHE_REFRESH_INTERVAL) {
          console.log(
            '[PermissionStore] Tab became visible and cache is stale, refreshing permissions',
          );
          Promise.all([state.loadPermissionGroups(true), state.loadUserPermissions(true)]).catch(
            (error) => {
              console.error('[PermissionStore] Visibility change refresh failed:', error);
            },
          );
        } else {
          console.log(
            '[PermissionStore] Tab became visible but cache is still fresh, skipping refresh',
          );
        }
      }, 500); // 500ms debounce
    }
  });
}

// Export timer control functions for manual control if needed
export { setupPermissionRefresh, clearPermissionRefresh };
